/**
 * The Members table's rows and actions (firm-model spec §10.2), derived purely from the
 * members response so the rules are tested without rendering. The panel never compares
 * role names (spec §4.2): it offers the other role and lets the server enforce the
 * last-admin invariant.
 */
import type { FirmRole } from "@contract/firm/me";
import type { MembersResponse } from "@contract/firm/members";

type RowAction = { kind: "set_role"; role: FirmRole } | { kind: "remove" } | { kind: "resend" } | { kind: "revoke" };

type MemberRow = {
  key: string;
  /** The member's name; "" for an invite or a member without one (the frames' Name column). */
  name: string;
  email: string;
  role: FirmRole;
  status: "Active" | "Invited" | "Disabled" | "Expired";
  /** Last Active: the day a member was last seen, or the day an invite went out. */
  lastActive: string;
  /** Credits used this period (A6, spec §9); "" for an invite or a pre-A6 server. */
  credits: string;
  actions: RowAction[];
  /** The user id (members) or invite id (invites) the actions address. */
  targetId: string;
};

export const ROLE_LABEL: Record<FirmRole, string> = { admin: "Admin", member: "Member" };
const OTHER_ROLE: Record<FirmRole, FirmRole> = { admin: "member", member: "admin" };

const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const day = (iso: string) => DAY.format(new Date(iso));

export function memberRows(data: MembersResponse, myUserId: string): MemberRow[] {
  const rows: MemberRow[] = data.members.map((m) => ({
    key: `m:${m.userId}`,
    name: m.name ?? "",
    email: m.email,
    role: m.role,
    status: m.status === "active" ? "Active" : "Disabled",
    lastActive: m.lastSeenAt ? day(m.lastSeenAt) : "Not yet",
    credits: m.creditsUsed === null ? "" : m.creditsUsed.toLocaleString("en-US"),
    actions:
      m.status === "active"
        ? [{ kind: "set_role", role: OTHER_ROLE[m.role] } as RowAction].concat(
            m.userId === myUserId ? [] : [{ kind: "remove" }],
          )
        : [],
    targetId: m.userId,
  }));
  for (const inv of data.invites) {
    rows.push({
      key: `i:${inv.id}`,
      name: "",
      email: inv.email,
      role: inv.role,
      status: inv.expired ? "Expired" : "Invited",
      lastActive: `Invited ${day(inv.invitedAt)}`,
      credits: "",
      actions: [{ kind: "resend" }, { kind: "revoke" }],
      targetId: inv.id,
    });
  }
  return rows;
}

/** The credits column's heading note: the period the numbers count from. */
export function creditsPeriodLabel(data: MembersResponse): string {
  if (data.members.every((m) => m.creditsUsed === null)) return "";
  return data.creditsSince ? `Credits used since ${day(data.creditsSince)}.` : "Credits used during the trial.";
}

export function actionLabel(action: RowAction): string {
  switch (action.kind) {
    case "set_role":
      return `Make ${ROLE_LABEL[action.role].toLowerCase()}`;
    case "remove":
      return "Remove";
    case "resend":
      return "Re-send invite";
    case "revoke":
      return "Revoke invite";
  }
}

/** Invites that expire within `days` (Home's Needs attention, spec §10.2). */
export function invitesExpiringSoon(data: MembersResponse, now: Date, days = 3): number {
  const limit = now.getTime() + days * 24 * 60 * 60 * 1000;
  return data.invites.filter((i) => !i.expired && new Date(i.expiresAt).getTime() <= limit).length;
}
